import React, { useEffect } from "react";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { Container, Typography, Button, Box, useTheme, useMediaQuery } from "@mui/material";
import Swal from "sweetalert2";
import FormTextField from "../components/form/FormTextField";
import FormSelect from "../components/form/FormSelect";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { setAlert } from "../features/slices/alertSlice";
import { useTranslation } from "react-i18next";
import {
  useGetBankAccountsQuery,
  useGetProfileQuery,
  usePlayerBanksMutation,
} from "../features/api/userApiSlice";
import { useNavigate, useLocation } from "react-router-dom";
import * as Constant from "../features/constant";

// Standalone "add bank" page used when REACT_APP_BANK_REGISTER === "true".
// Shows only the form (no existing-bank list); on success it redirects home,
// where the Layout guard re-checks and lets the user through.
const AddBankAccount = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));

  const { data: bankDetails } = useGetBankAccountsQuery();

  // The App-level bank gate bounces a bankless user back here (with
  // state.bankRequired) whenever they click something that navigates away.
  useEffect(() => {
    if (location.state?.bankRequired) {
      Swal.fire({
        title: t("walletcard.bankaccountpage.bankrequiredtitle", "Bank Account Required"),
        text: t(
          "walletcard.bankaccountpage.bankrequiredmessage",
          "Please add your bank account before continuing."
        ),
        icon: "warning",
      });
      // Drop the flag so a reload / back-forward doesn't re-open the popup.
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.key]);

  const { data: user } = useGetProfileQuery({ prefetch: true });

  const bankBsbEnabled = process.env.REACT_APP_BANK_BSB_ENABLED === "true";
  const bankAccountUseMobile = process.env.REACT_APP_BANK_ACCOUNT_USE_MOBILE === "true";

  const bankSchema = yup.object().shape({
    accountname: yup.string().required(t("validation.bankaccname")),
    bankcode: yup.string().required(t("validation.bankname")),
    bankaccount: yup.string().when("bankcode", {
      is: (bankcode) => {
        const isCrypto =
          bankDetails?.result?.bankConfigs?.find(
            (config) => config.bankCode === bankcode
          )?.isCrypto === "true";
        return isCrypto;
      },
      then: yup.string(),
      otherwise: yup
        .string()
        .required(t("validation.bankaccno"))
        .matches(/^[0-9]+$/, t("validation.bankaccnoonlynumbers")),
    }),
    bankbsb: bankBsbEnabled
      ? yup.string().required(t("validation.bankbsb"))
      : yup.string(),
  });

  const [playerBanks, { isLoading, isError, isSuccess, error }] =
    usePlayerBanksMutation();

  const { control, handleSubmit, setValue } = useForm({
    resolver: yupResolver(bankSchema),
  });

  // Keep the (disabled) account-name field in sync with the current profile —
  // defaultValue is only read on mount, so without this it would show a stale
  // name if the profile query resolves after mount (e.g. right after login).
  useEffect(() => {
    setValue("accountname", user?.result?.fullname || "");
  }, [user?.result?.fullname]);

  useEffect(() => {
    if (bankAccountUseMobile && user?.result?.mobileNumber) {
      setValue("bankaccount", user.result.mobileNumber);
    }
  }, [user?.result?.mobileNumber, bankAccountUseMobile]);

  const submitBank = async (data) => {
    const newData = {
      bankCode: data?.bankcode,
      bankAccountNumber: data?.bankaccount,
      bankAccountName: data?.accountname,
      ...(bankBsbEnabled && { bankAccountBsb: data?.bankbsb }),
    };

    try {
      await playerBanks(newData).unwrap();
    } catch {
      // error surfaced via the isError effect below
    }
  };

  useEffect(() => {
    if (!isLoading && isSuccess) {
      dispatch(
        setAlert({
          isOpen: true,
          message: t("walletcard.bankaccountpage.successmessage"),
          variant: "success",
        })
      );
      navigate(Constant.getPath("/"), { replace: true });
    }
    if (isError) {
      dispatch(
        setAlert({
          isOpen: true,
          message: error?.data?.message,
          variant: "error",
        })
      );
    }
  }, [isLoading, isSuccess, isError]);

  return (
    <Container
      sx={{
        py: { xs: 9, sm: 4 },
      }}
    >
      <Typography
        fontWeight="bold"
        fontSize={mobileView ? "large" : "x-large"}
        color={process.env.REACT_APP_COLOR_TEXT || "white"}
      >
        {t("walletcard.bankAccount")}
      </Typography>

      <Box display="flex" flexDirection="column">
        <form onSubmit={handleSubmit(submitBank)}>
          <FormTextField
            label={t("walletcard.bankaccountpage.accountname")}
            control={control}
            controllerName="accountname"
            defaultValue={user?.result?.fullname}
            size="small"
            disabled={true}
            InputLabelProps={{ shrink: true }}
          />
          <FormSelect
            label={t("walletcard.bankaccountpage.bankname")}
            control={control}
            controllerName="bankcode"
            items={bankDetails?.result?.bankConfigs?.map((item) => item.bankCode)}
            size="small"
          />
          <FormTextField
            label={t("walletcard.bankaccountpage.bankaccount")}
            control={control}
            controllerName="bankaccount"
            defaultValue={""}
            size="small"
            disabled={bankAccountUseMobile}
            InputLabelProps={bankAccountUseMobile ? { shrink: true } : undefined}
            sx={
              bankAccountUseMobile
                ? {
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor:
                      process.env.REACT_APP_COLOR_DISABLE || "#9e9e9e",
                  },
                }
                : undefined
            }
          />
          {bankBsbEnabled && (
            <FormTextField
              label={t("walletcard.bankaccountpage.bankbsb")}
              control={control}
              controllerName="bankbsb"
              defaultValue={""}
              size="small"
            />
          )}
          <Box alignSelf="center" mb="15px" mt="15px">
            <Button variant="contained" type="submit" disabled={isLoading}>
              {t("walletcard.submit")}
            </Button>
          </Box>
        </form>
      </Box>
    </Container>
  );
};

export default AddBankAccount;
